import React, { Fragment, useState, useContext } from "react";

import { Form, Button } from "react-bulma-components";

import TrimmedInput from "common-frontend/util/trimmedInput";
import ErrorMessage from "util/errorMessage";

import { participationStateContext } from "./load";

const { Field, Label, Control } = Form;

// The exported <AskFeeQualifyingData> component is rendered for each
// registration item, and it renders the internal AskFeeQualifyingDataPiece
// component for each askFor specification in the item's ask_for list (usually
// either none or a single "discount code" data piece).

// The askFor specification has:
//    * "key_string" specifying where in the the item's fee qualifying
//       data object this particular piece of data is to go,
//    * "previous_submission" specifying what used by the server in
//      generating the current set of fee category information
//    * "previous_submission_not_accepted" holding an error message if
//      the previous submission was not valid
//    * various labels used in the prompting for the data piece.

// In addition to AskFor, the AskFeeQualifyingDataPiece component is also
// given paramKeys, the list of keys with which the corresponding piece of
// submitted data can be accessed and manipulated within the overal loadParams
// parameter state maintained by useLoadParticipation. This will be

//    ["fee_qualifying_data", the item's key string, askFor.key_string ]

// Because fee qualifying data also needs to be submitted with the final
// registration, not just when fetching the data used to prepare the
// registration form, this module also provides an adjustFeeQualifyingDetails
// function that the registration item component should call whenever it
// adjusts the set of details to be submitted. This will copy each value
// the server used in generating the current participation data,
// from "previous_submission" in each entry in the ask_for list, into the
// fee_selection_data section of the details.

function AskFeeQualifyingDataPiece({ paramKeys, askFor }) {
  const { paramState } = useContext(participationStateContext);

  const currentlySubmittedValue = paramState.getSubmitted(paramKeys);
  const currentlyEffectiveValue = paramState.getEffective(paramKeys);
  const setSubmittedValue = (v) => paramState.setSubmitted(paramKeys, v);

  const currentlyUsedValue = askFor.previous_submission;
  const [currentlyEnteredValue, setCurrentlyEnteredValue] = useState(
    currentlyUsedValue
  );

  const pending =
    currentlyEnteredValue === currentlySubmittedValue &&
    currentlySubmittedValue !== currentlyEffectiveValue;

  const notAccepted =
    currentlyEnteredValue === currentlyUsedValue
      ? askFor.previous_submission_not_accepted
      : null;

  return (
    <Field>
      <Label>{askFor.label}</Label>
      <Field kind="group">
        <Control>
          <TrimmedInput
            value={currentlyEnteredValue ?? ""}
            setValue={(v) => setCurrentlyEnteredValue(v === "" ? undefined : v)}
          />
        </Control>
        <Control>
          <Button
            invisible={(currentlyEnteredValue ?? null) === currentlyUsedValue}
            disabled={pending}
            onClick={() =>
              setSubmittedValue(
                (currentlyEnteredValue ?? "") === ""
                  ? null
                  : currentlyEnteredValue
              )
            }
          >
            {pending ? <i>Checking ...</i> : askFor.apply_label}
          </Button>
        </Control>
      </Field>
      {notAccepted ? <ErrorMessage>{notAccepted}</ErrorMessage> : null}
    </Field>
  );
}

export function AskFeeQualifyingData({ item }) {
  const { buildItemKey } = useContext(participationStateContext);
  const itemKey = buildItemKey(item);

  return (
    <Fragment>
      {item.ask_for.map((askFor, i) => (
        <AskFeeQualifyingDataPiece
          key={i}
          {...{
            paramKeys: ["fee_qualifying_data", itemKey, askFor.key_string],
            askFor,
          }}
        />
      ))}
    </Fragment>
  );
}

export function adjustFeeQualifyingDetails(details, item) {
  item.ask_for.forEach(
    (askFor) =>
      (details.fee_selection_data[askFor.key_string] =
        askFor.previous_submission)
  );
}
